<template>
<div class="product-pricing-page">
  <page-content-header
    :item="item"
  />
  <b-container>
    <b-row>
      <b-col>
        <page-content-creator
          :itemid="itemid"
          pos="top"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <node-element-local-tasks
          v-if="itemid"
          :itemid="itemid"
        />
        <div
          v-if="notpublished"
          class="messages warning"
        >
          Not published
        </div>
      </b-col>
    </b-row>
  </b-container>
  <section class="pricing-section">

  <div class="billing-region">
    <b-container>
      <b-row>
        <b-col>
          <div class="billing-container">
            <div class="billing-selector">
              <button
                v-for="(label, key) in billingOptions"
                @click="changeBilling(key)"
                :key="key"
                :class="activeBilling(key)"
                class="billing-button"
              >
                {{ label }}
              </button>
            </div>
            <div
              v-if="savings"
              v-html="savings"
              class="savings"
            />
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
  <div class="pricing-plans-region">
  <b-container >
    <b-row>
      <b-col>
        <div
          :class="{ 'has-featured': hasFeaturedPlan }"
          class="plans"
        >
            <div
              v-for="(plan, index) in billingPlans"
              :key="`billingPlan-${index}`"
              class="plan-container"
            >
              <node-element-local-tasks
                v-if="getPlanId(plan)"
                :itemid="getPlanId(plan)"
                :options="{ destination: localTaskLink }"
              />
              <div
                :class="planClasses(plan, index)"
                class="plan"
              >
                <div class="plan-header">
                  <div class="plan-icon">
                    <AImage
                      :img="plan.plan.icon"
                      :alt="plan.plan.title"
                      :title="plan.plan.title"
                      cover
                      contain
                      responsive
                    />
                  </div>
                  <div class="plan-title">
                    <h3>{{ plan.plan.title }}</h3>
                  </div>
                  <div
                    v-if="plan.plan.subtitle"
                    class="plan-subtitle"
                  >
                    {{ plan.plan.subtitle }}
                  </div>
                  <div
                    v-if="plan.showprice"
                    class="plan-price-element"
                  >
                    <span class="plan-currency">$</span>
                    <span class="plan-price">{{ plan.price || 'N/A' }}</span>
                    <span>/ {{ periods[plan.period] }}</span>
                  </div>
                  <div
                    v-else
                    class="plan-price-element"
                  >
                    <div class="plan-call">{{ plan.nopricecta || 'N/A' }}</div>
                  </div>
                  <div v-if="plan.plan.setupcost"
                    class="plan-setup-cost"
                  >
                    {{ plan.plan.setupcost }}
                  </div>
                  <component
                    v-if="plan.action.url && plan.action.title"
                    :is="linkComponent"
                    :to="{ path: plan.action.url }"
                    :href="plan.action.url"
                    class="btn plan-button"
                  >
                    {{ plan.action.title }}
                  </component>
                </div>
                <div class="feature-set">
                  <div
                    v-if="plan.plan.body"
                    v-html="plan.plan.body"
                    class="feature-description"
                  />
                  <div class="feature-list-section">
                    <div class="feature-list-title">
                      <h4>{{ plan.plan.featureheading }}</h4>
                    </div>
                    <ul class="feature-list">
                      <li
                        v-for="(feature, index) in getFeatures(plan, index)"
                        :key="index"
                        class="feature-list-item"
                      >
                        <span class="feature-list-item-bullet"></span>
                        <span class="feature-list-item-description">{{ feature }}</span>
                      </li>
                    </ul>
                    <template v-if="canCollapseFeatures(plan)">
                      <button
                        v-if="isFeatureExpanded(index)"
                        class="more-features show-less"
                        @click="showLess(index)"
                      >
                        Show Less
                      </button>
                      <button
                        v-else
                        class="more-features show-more"
                        @click="showMore(index)"
                      >
                        Show More
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
  </section>
  <b-container>
    <b-row>
      <b-col>
        <page-content-creator
          :itemid="itemid"
          pos="bottom"
        />
      </b-col>
    </b-row>
  </b-container>
</div>
</template>
<script>
import { asyncLoader, pageMixin, getPreloadImageUrlPromises } from "../../special-pages-common";

const gqlDoc = `
  query pricingpageitem($id: ID!) {
    pricingpageitem(id: $id) {
      id
      header {
        image {
          filestorename
          filepath
          preset
        }
        content
        links {
          link {
            title
            url
          }
        }
      }
      featurelimit
      savings
      plans {
        delta
        frequency
        featured
        plan {
          id
          title
          body
          subtitle
          icon {
            filestorename
            filepath
            preset
          }
          setupcost
          featureheading
          features
          pricingplanfilestore
        }
        showprice
        price
        period
        nopricecta
        action {
          url
          title
        }
      }
    }

    frequency : __type(name: "PricingPlanFrequency") {
      enumValues {
        name
        description
      }
    }

    period: __type(name: "PricingPlanPricePeriod") {
      enumValues {
        name
        description
      }
    }
  }
`

function preloadPricingData({store}, itemid) {
  applogger.log('sp-pricing: pricing data loading started')
  return store.getters['getGraphQLClient'](null, gqlDoc).query({opName: 'pricingpageitem', variables: {id: itemid}})
    .then((response) => {
      if (response.errors) {
        const errors = response.errors.map(err => err.message).join('\n')
        throw new Error(pricing.errors[0].message)
      }

      const data = {
        pricing: response.data.pricingpageitem,
        frequency: response.data.frequency.enumValues.reduce((acc, {name, description}) => ({
            ...acc,
            [name]: description
          }), {}),
        period: response.data.period.enumValues.reduce((acc, {name, description}) => ({
            ...acc,
            [name]: description
          }), {})
      }

      return Promise.all([
        store.dispatch('adhocstore', {key: `pricing-page-${itemid}`, data: data}),
        getPreloadImageUrlPromises(
          store,
          ['banner', '__original'],
          [[data.pricing.header.image], data.pricing.plans.map(plan => (plan && plan.plan && plan.plan.icon) ? plan.plan.icon : null).filter(icon => icon)]
        )
      ])
      .then(_ => applogger.log('sp-pricing: pricing data loading complete'))
    })
    .catch(err => {
      applogger.error(err)
      applogger.error(`sp-pricing: pricing data loading failed`)
    })
}

export default {
  name: 'page-content-type-sp_pricing',
  mixins: [
    pageMixin
  ],
  asyncData: (args) => {
    if ( !args.route )  {
      return Promise.resolve(false)
    }
    return args.store.dispatch('promisePool', {
      name: `page-content-type_sp_pricing--${args.route.path}`,
      resolve: () => asyncLoader(args)
        .then(({itemid}) => preloadPricingData(args, itemid))
    })
  },
  data() {
    return {
      selectedBilling: null,
      featureExpanded: {}
    }
  },
  computed: {
    linkComponent() {
      if (/^#|http(s)?:\/\//.test(this.plan.action.url)) return 'a'
      return 'router-link'
    },
    localTaskLink() {
      if (this.$route) {
        return this.$route.path || null
      }
      return null
    },
    data() {
      return this.$store.getters['getAdHocStorageValue'](`pricing-page-${this.itemid}`)
    },
    hasPricing() {
      return Object.keys(this.pricing).length > 0
    },
    hasPlans() {
      return this.hasPricing && this.plans.length > 0
    },
    pricing() {
      return this.item ? this.data.pricing : []
    },
    frequencies() {
      return this.item ? this.data.frequency : {}
    },
    periods() {
      return this.item ? this.data.period : {}
    },
    plans() {
      return this.item ? this.pricing.plans : []
    },
    savings() {
      return this.item ? this.pricing.savings : {}
    },
    billingOptions() {
      if ( this.item ) {
        return this.plans.reduce((acc, {frequency}) => ({...acc, [frequency]: this.frequencies[frequency]}), {})
      }
      return {}
    },
    billingPlans() {
      if ( this.item ) {
        return this.plans.filter(plan => plan.frequency === this.selectedBilling)
      }
      return []
    },
    hasFeaturedPlan() {
      if (this.billingPlans) {
        return this.billingPlans.some(plan => plan.featured)
      }
      return false
    }
  },
  methods: {
    changeBilling(billing) {
      this.selectedBilling = billing
      this.$set(this, 'featureExpanded', {})
    },
    activeBilling(billing) {
      return this.selectedBilling === billing ? 'active' : ''
    },
    planClasses(plan, index) {
      let classes = [
        `plan-${index + 1}`
      ]

      if (plan.featured) {
        classes.push('plan-featured')
      }

      return classes
    },
    getFeatures(plan, index) {
      if (this.isFeatureExpanded(index) || !this.canCollapseFeatures(plan)) {
        return plan.plan.features
      }
      return plan.plan.features.slice(0, this.pricing.featurelimit)
    },
    getPlanId(plan) {
      if (plan && plan.plan && plan.plan.id) {
        return plan.plan.id
      }
      return null
    },
    canCollapseFeatures(plan) {
      return plan.plan.features.length > this.pricing.featurelimit
    },
    isFeatureExpanded(index) {
      if (index != null) {
        return this.featureExpanded[index]
      }
      return true
    },
    showMore(index) {
      if (index != null) {
        this.$set(this.featureExpanded, index, true)
      }
    },
    showLess(index) {
      if (index != null) {
        this.$set(this.featureExpanded, index, false)
      }
    },
  },
  created() {
    this.item = this.$store.getters['node/getByPath'](this.$route.path)
    if (this.hasPlans) {
      this.selectedBilling = this.plans[0].frequency
    }
  }
}
</script>
<style lang="scss">
:root {
  --main-plans-link-color: var(--main-link-color);

  --main-plans-featured-highlight-background-color: var(--main-highlight-background-color);
  --main-plans-featured-highlight-color: var(--main-highlight-background-color);
  --main-plans-featured-highlight-text-color: var(--main-highlight-text-color);

  --main-plans-featured-highlight-hover-background-color: var(--main-highlight-hover-background-color);
  --main-plans-featured-highlight-hover-color: var(--main-highlight-background-hover-color);
  --main-plans-featured-highlight-hover-text-color: var(--main-highlight-hover-text-color);

  --main-plans-featured-link-color: var(--main-highlight-color);
  --main-plans-section-background: var(--main-light-background-color);
  --main-plans-featured-icon-fill-color: var(--main-plans-featured-highlight-color);
  --main-plans-icon-fill-color:var(--main-link-color);

  //main-plan button
  --main-plans-button-background: var(--main-link-color);
  --main-plans-button-text-color:#ffffff;
  --main-plans-button-hover-background:var(--main-link-hover-color);
  --main-plans-button-hover-text-color:#ffffff;


//featured-plan button
  --main-plans-featured-button-background: var(--main-plans-featured-highlight-color);
  --main-plans-featured-button-text-color: var(--main-plans-featured-highlight-text-color);

  --main-plans-featured-button-hover-background: var(--main-plans-featured-highlight-hover-background-color);
  --main-plans-featured-button-hover-text-color: var(--main-plans-featured-highlight-hover-text-color);

  --main-plans-cards-heading-background: #ffffff;
  --main-plans-cards-body-background: var(--main-plans-cards-heading-background);
  --main-plans-featured-text-color: var(--main-plans-featured-highlight-color);



  //--main-page-header-banner-background: var(--main-plans-billing-region-background);

  --main-plans-billing-region-background: var(--main-light-background-color);
  --main-plans-billing-region-text-color: var(--main-body-color);
  --main-plans-billing-button-text-color:#ffffff;
  --main-plans-billing-button-background:var(--main-link-color);
  --main-plans-billing-button-hover-background:var(--main-link-color);
  --main-plans-billing-button-hover-text-color: #ffffff;
  --main-plans-billing-button-active-text-color:var(--main-link-color);
  --main-plans-billing-button-active-background:#ffffff;
  --main-plan-featured-offset-height: 4em;
}
</style>

<style scoped lang="scss">
@import '~@scss/custom.scss';

.product-pricing-page {
    background:var(--main-plans-section-background);
    .header-region {
      margin-bottom:0;
      background-color: var(--main-page-header-banner-background);
    }
}
.billing-region{
    padding:3em 0;
    background:var(--main-plans-billing-region-background);
    color: var(--main-plans-billing-region-text-color);
    margin-bottom: var(--main-plan-featured-offset-height);
    padding-bottom: calc(var(--main-plan-featured-offset-height) * 2);
}

.pricing-plans-region{
  margin-bottom:3em;
}
.billing-button{
  padding:1em;
  cursor: pointer;
  display: inline-flex;
  outline: none !important;
  border:0px;
  //background-color: rgba(0, 0, 0, 0.15);
  //color:rgba(255, 255, 255, 0.5);
  background-color: var(--main-plans-billing-button-background);
  color:var(--main-plans-billing-button-text-color);

  @include transition ((background-color, color, .3s));
  @include hover-focus {
  background-color: var(--main-plans-billing-button-hover-background);
  }
  &.active{
    background-color: var(--main-plans-billing-button-active-background);
    color:var(--main-plans-billing-button-active-text-color);
    cursor: default;
  }
  &:first-child{
    padding-left:2em;
    padding-right:1.5em;
    border-radius:6px 0em 0em 6px;
  }
  &:last-child{
    padding-right:2em;
    padding-left:1.5em;
    border-radius:0 6px 6px 0em;
  }
  &:not(:first-child):not(:last-child){
    border-right:1px solid rgba(0, 0, 0, 0.2);
    border-left:1px solid rgba(0, 0, 0, 0.2);
  }
}
.billing-container {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding-bottom:1rem;
  .billing-selector {
    margin-bottom:1.5rem;
  }
  .savings {
    margin-bottom:1rem;
    p {
      &:last-child {
        margin:0;
      }
    }
  }
}


.btn.plan-button {
  display:flex;
  justify-content: center;
  margin:0 auto;
  background: var(--main-plans-button-background);
  color: var(--main-plans-button-text-color);
  @include transition((background .3s, color .3s));
  border-radius: 5em;
  padding:1em;
  font-size:1em;
  max-width: 80%;
  @include media-breakpoint-up(lg) {
    padding: 1em 1.5em;
    font-size: 1.1em;
  }
  @include media-breakpoint-up(xl) {
    padding: 1em 2.5em;
    max-width: 80%;
  }
  @include hover-focus {
  color: var(--main-plans-button-hover-text-color);
  background: var(--main-plans-button-hover-background);
  }
}
.plan-icon {
  height:9em;
  width:9em;
  border-radius:50% 0% 50% 50%;
  //border:2px solid;
  margin: 0 auto 1em auto;

}
.plan-currency{
  font-size: 2em;
}
.plan-price, .plan-call{
  font-size: 3.25em;
}
.plans {
  top:6em;
  display: flex;
  //margin-top:-8em;
  flex-direction: column;
  @include media-breakpoint-up(md) {
    //margin-top:-10em;
  }
  @include media-breakpoint-up(lg) {
    flex-direction: row;
    box-shadow: 0 10px 90px 10px rgba(0,0,0,0.05);
  }
  border-radius: 3px;
  .plan-container {
    position: relative;
    display:flex;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0%;
  }

}
.plan {
  display: flex;
  flex-direction: column;
  width: 100%;

  @include media-breakpoint-down(md) {
    margin-bottom:2em;
    overflow: hidden;
    border-radius:3px;
  }
}
.plan-header{
  text-align: center;
  padding: 3em 2em 2em 2em;
  background: var(--main-plans-cards-heading-background);
  min-height:10em;
  word-break: break-all;
  word-break: break-word;

}
.plan-title{
  h3 {
    font-size:2em;
  }
}
.plan-subtitle{
  font-style: italic;
}
.plan-setup-cost {
  margin-bottom:1em;
}
@include media-breakpoint-up(lg) {
  .plans {
    .plan-container {
      &:first-child {
        .plan-header {
          border-radius: 6px 0px 0px 0px;
        }

        .feature-set {
          border-radius: 0px 0px 0px 3px;
        }
      }
      &:last-child {
        .plan-header {
          border-radius: 0px 6px 0px 0px;
        }

        .feature-set {
          border-radius: 0px 0px 3px 0px;
        }
      }
    }
      margin-top: calc(#{var(--main-plan-featured-offset-height)} * -3);
    &.has-featured {
      margin-top: calc(#{var(--main-plan-featured-offset-height)} * -2);
    }
  }
}
.plan-price-element{
  line-height: 1;
  margin: 1.5em 0em;
}
.plan-featured {
  @include media-breakpoint-up(lg) {
    box-shadow: 0 10px 40px 5px rgba(0,0,0,0.1);
    z-index: 2;
    margin-top: calc(#{var(--main-plan-featured-offset-height)} * -1); //offset it by minus this offset value
    .plan-header{
        border-radius: 6px 6px 0px 0px !important;
    }
  }

  .plan-price-element {
    color:var(--main-plans-featured-text-color);
  }
  .btn.plan-button{
    background: var(--main-plans-featured-button-background);
    color:var(--main-plans-featured-button-text-color);
    @include hover-focus {
    color: var(--main-plans-featured-button-hover-text-color);
    background: var(--main-plans-featured-button-hover-background);
  }
  }

.feature-list-section {
  .feature-list {
    &-item-bullet{
        border-color: var(--main-plans-featured-icon-fill-color);
    }

  }
}
.more-features {
  color: var(--main-plans-featured-link-color);
}

}
.feature-set {
  flex: 1;
  display: flex;
  flex-direction: column;
  background:var(--main-plans-cards-body-background);
  padding: 1.5em 2em;
  word-break: break-all;
  word-break: break-word;

  .feature-description {
    margin-bottom:1.5em;
  }

}
.feature-list-section{
  .feature-list {
    list-style:none;
    padding:0;
    margin:1.5em 0;
    &-item-bullet{
      display:inline-flex;
      margin-top: .4em;
      margin-right: .75em;
      height: 1em;
      width: 1em;
      border:2px solid;
      border-color: var(--main-plans-icon-fill-color);
      border-radius:100% 0 100% 100%;
      flex-shrink: 0;
    }
    &-item{
      display:flex;
      align-items: flex-start;
      &:not(:last-child){
        margin-bottom:.75em;
      }
    }
    &-item-description {
      word-break: break-all;
      word-break: break-word;
    }

  }
}

    .more-features {
      padding:0;
      border:0;
      color:var(--main-plans-link-color);
      background:none;
      outline:none;
    }
</style>
